// src/pages/Dashboard/DashboardHome/components/YearlyMetricsCards.tsx
import React, { useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Repeat, Users } from "lucide-react";
import { Card } from "@/components/Card";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import ContentLoader from "react-content-loader";

interface YearlyMetricsCardsProps {
  year: number;
}

interface CardInfo {
  title: string;
  value: string | number;
  changeLabel?: string;
  icon: React.ReactNode;
}

interface YearlyMetrics {
  year: number;
  mrr: number;
  ticketMedio: number;
  activeRecurringSubscriptions: number;
  purchasesInYear: number;
}

function buildDefaultYearlyCards(year: number): CardInfo[] {
  return [
    {
      title: `MRR Médio ${year}`,
      value: "R$ 0,00",
      changeLabel: `0 inscrições ativas em ${year}`,
      icon: <Repeat size={20} />,
    },
    {
      title: `Ticket Médio ${year}`,
      value: "R$ 0,00",
      changeLabel: `considerando 0 compras esse ano`,
      icon: <Users size={20} />,
    },
  ];
}

function toNumberOrZero(v: any): number {
  if (v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function transformYearlyData(raw: any, year: number): CardInfo[] {
  const metrics: YearlyMetrics = raw?.yearlyMetrics;
  if (!metrics) return buildDefaultYearlyCards(year);

  const mrr = toNumberOrZero(metrics.mrr);
  const ticketMedio = toNumberOrZero(metrics.ticketMedio);
  const activeRecurring = toNumberOrZero(metrics.activeRecurringSubscriptions);
  const purchases = toNumberOrZero(metrics.purchasesInYear);

  return [
    {
      title: `MRR Médio ${year}`,
      value: `R$ ${mrr.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      changeLabel: `${activeRecurring} ${activeRecurring === 1 ? 'inscrição ativa' : 'inscrições ativas'} em ${year}`,
      icon: <Repeat size={20} />,
    },
    {
      title: `Ticket Médio ${year}`,
      value: `R$ ${ticketMedio.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      changeLabel: `considerando ${purchases} ${purchases === 1 ? 'compra' : 'compras'} esse ano`,
      icon: <Users size={20} />,
    },
  ];
}

async function fetchYearlyData(coworkingId: string, token: string, year: number): Promise<CardInfo[]> {
  // Pass year to get yearly metrics for that specific year
  const path = `/coworking/${encodeURIComponent(coworkingId)}/dashboard?year=${year}`;
  const resp = await api.get(path, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const raw = (resp && (resp as any).data) ? (resp as any).data : resp;
  return transformYearlyData(raw, year);
}

const YearlyMetricsCards: React.FC<YearlyMetricsCardsProps> = ({ year }) => {
  const userContext = useUser();
  const userData: any = userContext?.userData;
  const token = userData?.auth;
  const coworkingId = userData?.coworkingId ?? null;
  
  const lastSuccessfulDataRef = useRef<CardInfo[]>([]);

  const { data: cardsInfo, isLoading, isFetching, error } = useQuery({
    queryKey: ['yearlyMetrics', coworkingId, year],
    queryFn: () => fetchYearlyData(coworkingId!, token!, year),
    enabled: !!coworkingId && !!token,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (cardsInfo && cardsInfo.length > 0) {
      lastSuccessfulDataRef.current = cardsInfo;
    }
  }, [cardsInfo]);

  const displayData = (cardsInfo && cardsInfo.length > 0) ? cardsInfo : lastSuccessfulDataRef.current;
  const hasData = displayData.length > 0;
  const loading = isLoading && !hasData;
  const cardsToRender = hasData ? displayData : buildDefaultYearlyCards(year);

  function listCards(cards: CardInfo[]) {
    return (
      <>
        {cards.map((card, index) => (
          <Card key={index} className="stat-card yearly-stat-card">
            <div className="card-header">
              <h3 className="card-title">{card.title}</h3>
              <div className="card-icon">{card.icon}</div>
            </div>
            <div className="card-content">
              <div className="stat-value">{card.value}</div>
              {card.changeLabel && (
                <p className="stat-change stat-info">{card.changeLabel}</p>
              )}
            </div>
          </Card>
        ))}
      </>
    );
  }

  function listSkeletons() {
    return (
      <>
        {Array.from({ length: 2 }).map((_, idx) => (
          <Card key={idx} className="stat-card yearly-stat-card">
            <ContentLoader speed={2} width="100%" height={100} backgroundColor="#f3f3f3" foregroundColor="#ecebeb">
              <rect x="10" y="10" rx="4" ry="4" width="70%" height="16" />
              <circle cx="90%" cy="18" r="14" />
              <rect x="10" y="40" rx="4" ry="4" width="50%" height="24" />
              <rect x="10" y="75" rx="4" ry="4" width="60%" height="12" />
            </ContentLoader>
          </Card>
        ))}
      </>
    );
  }

  return (
    <div className={`yearly-metrics-column ${isFetching && !isLoading ? 'refetching' : ''}`}>
      {loading ? listSkeletons() : listCards(cardsToRender)}
    </div>
  );
};

export default YearlyMetricsCards;
